import cx from "classnames"
import type React from "react"
import { useRef, useState } from "react"
import Modal, { type ModalPropsType } from "."

type StepComponentType<T> = (props: {
  navigation: {
    nextStep: () => void
    previousStep: () => void
    closeModal: () => void
  }
  data: T
}) => React.ReactNode

export type ModalStepType<T> = {
  title: StepComponentType<T>
  content: StepComponentType<T>
  footer?: StepComponentType<T>
}

type MultiStepsModalProps<T = null> = {
  stepData?: T
  close: () => void
  onHidden?: (currentStepIndex: number) => void
  steps: ModalStepType<T>[]
  useMidSizeHeight?: boolean
} & Omit<ModalPropsType, "children" | "onHidden" | "close">

const MultiStepModal = <T,>({
  stepData = null as T,
  close,
  steps,
  onHidden,
  useMidSizeHeight = true,
  ...modalProps
}: MultiStepsModalProps<T>) => {
  const bodyScrollContentRef = useRef<HTMLDivElement>(null)
  const [currentStep, setCurrentStep] = useState(0)

  const handleNextStep = () => {
    setCurrentStep(
      currentStep >= steps.length - 1 ? currentStep : currentStep + 1,
    )
  }

  const handlePreviousStep = () => {
    setCurrentStep(currentStep <= 0 ? 0 : currentStep - 1)
  }

  const onModalClosed = () => {
    onHidden?.(currentStep)
    setCurrentStep(0)
  }

  const navigation = {
    nextStep: handleNextStep,
    previousStep: handlePreviousStep,
    closeModal: close,
  }

  const CurrentStepComponents = steps[currentStep]
  const stepProps = { data: stepData, navigation }

  const minimumScale = useMidSizeHeight
    ? "c-opacity-0 c-grid-rows-[0.5fr]"
    : "c-opacity-0 c-grid-rows-[0fr]"

  return (
    <Modal
      {...modalProps}
      title={<CurrentStepComponents.title {...stepProps} />}
      close={close}
      onHidden={onModalClosed}
    >
      <div className="c-w-full c-overflow-hidden">
        <div
          className="c-py-sm c-transition-transform c-duration-500 c-ease-in-out"
          ref={bodyScrollContentRef}
          style={{
            width: `${steps.length * 100}%`,
            transform: `translateX(-${(100 / steps.length) * currentStep}%)`,
          }}
        >
          {steps.map((StepComponents, index) => {
            return (
              <div
                key={`step-${index}-${steps[index].title.toString()}`}
                className={cx(
                  "c-inline-grid c-align-top c-transition-all c-duration-500",
                  {
                    "c-opacity-100 c-grid-rows-[1fr]": index === currentStep,
                    [minimumScale]: index !== currentStep,
                  },
                )}
                style={{ width: `${100 / steps.length}%` }}
              >
                <div className="c-overflow-hidden">
                  <StepComponents.content {...stepProps} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {CurrentStepComponents.footer && (
        <Modal.Footer>
          <CurrentStepComponents.footer {...stepProps} />
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default MultiStepModal
